import { useState } from "react";
import { Search, BookOpen } from "lucide-react";
import { METRICS, METRIC_CATEGORIES } from "@/lib/metricsGlossary";

export default function Glossary() {
    const [q, setQ] = useState("");

    const entries = Object.entries(METRICS).filter(([, m]) => {
        if (!q) return true;
        const needle = q.toLowerCase();
        return m.label.toLowerCase().includes(needle) || m.definition.toLowerCase().includes(needle);
    });

    const byCategory = METRIC_CATEGORIES.map((cat) => ({
        cat,
        items: entries.filter(([, m]) => m.category === cat),
    })).filter((g) => g.items.length > 0);

    return (
        <div className="space-y-8" data-testid="glossary-page">
            <div>
                <div className="overline flex items-center gap-2"><BookOpen className="w-3.5 h-3.5" /> Reference</div>
                <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Metrics Glossary</h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                    Every metric used across InPhase — what it means, why it matters, and exactly what to do to move it.
                </p>
            </div>

            <div className="relative max-w-md">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                    data-testid="glossary-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search metrics… e.g. LCP, backlinks, KD"
                    className="w-full bg-input border border-border pl-9 pr-3 py-2.5 text-sm font-mono focus:outline-none focus:border-primary"
                />
            </div>

            {byCategory.length === 0 && (
                <div className="text-sm text-muted-foreground" data-testid="glossary-empty">No metrics match "{q}".</div>
            )}

            <div className="space-y-10">
                {byCategory.map(({ cat, items }) => (
                    <div key={cat} data-testid={`glossary-category-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
                        <div className="overline text-primary mb-4">{cat}</div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {items.map(([key, m]) => (
                                <div key={key} className="border border-border bg-card/40 p-6" data-testid={`glossary-entry-${key}`}>
                                    <div className="font-display text-lg">{m.label}</div>
                                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{m.definition}</p>
                                    <div className="mt-4">
                                        <div className="text-[10px] uppercase tracking-widest text-primary mb-1">Why it matters</div>
                                        <p className="text-xs text-muted-foreground leading-relaxed">{m.whyItMatters}</p>
                                    </div>
                                    <div className="mt-4">
                                        <div className="text-[10px] uppercase tracking-widest text-primary mb-1">How to improve it</div>
                                        <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
                                            {m.howToImprove.map((tip, i) => <li key={i}>{tip}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
